/**
 * Thème du club.
 *
 * Applique les couleurs choisies par le club en garantissant la lisibilité :
 * le fond de l'application reste sombre, seules les couleurs d'accent changent.
 *
 *  - Principale trop sombre (bleu marine, noir…) : invisible sur le fond.
 *    On l'éclaircit jusqu'à un contraste suffisant.
 *  - Texte posé sur la principale : noir ou blanc, selon ce qui se lit le mieux.
 *    Un club en jaune aura des boutons à texte noir.
 *  - Secondaire trop sombre : on la remplace par une teinte dérivée de la
 *    principale, et on le signale à l'admin dans l'aperçu.
 *
 * Les couleurs de sens négatif (défaite, montant dû, erreur) ne sont jamais
 * touchées : elles restent rouges quel que soit le club.
 */

export const FOND = '#0d1117';
export const DEFAUT = { couleur: '#e63946', couleur2: null };
const GRIS_COURBE = '#8b949e';

// ── Conversions ─────────────────────────────────────────────────────────────
export function hexValide(h) { return typeof h === 'string' && /^#[0-9a-f]{6}$/i.test(h); }

function hexVersRgb(h) {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbVersHex([r, g, b]) {
  return '#' + [r, g, b].map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
}

// ── Lisibilité (formule WCAG) ───────────────────────────────────────────────
function luminance(hex) {
  const [r, g, b] = hexVersRgb(hex).map(v => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contraste(a, b) {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

function rgbVersTsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min, s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}
function tslVersRgb([h, s, l]) {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
  const f = t => { t = (t + 1) % 1;
    return 255 * (t < 1/6 ? p + (q - p) * 6 * t : t < 1/2 ? q : t < 2/3 ? p + (q - p) * (2/3 - t) * 6 : p); };
  return [f(h + 1/3), f(h), f(h - 1/3)];
}

/**
 * Éclaircit une couleur trop sombre jusqu'au contraste visé, en conservant
 * sa teinte : un bleu marine reste bleu au lieu de virer au gris.
 */
function eclaircir(hex, cible = 3) {
  if (contraste(hex, FOND) >= cible) return hex;
  const [h, s] = rgbVersTsl(hexVersRgb(hex));
  for (let l = 0.2; l <= 1; l += 0.02) {
    const c = rgbVersHex(tslVersRgb([h, s, l]));
    if (contraste(c, FOND) >= cible) return c;
  }
  return '#ffffff';
}

/**
 * Calcule le thème effectif à partir des couleurs saisies par le club.
 * Renvoie aussi des remarques destinées à l'aperçu de l'admin.
 */
export function calculerTheme(couleur, couleur2) {
  const remarques = [];
  const saisie = hexValide(couleur) ? couleur.toLowerCase() : DEFAUT.couleur;

  const accent = eclaircir(saisie, 3);
  if (accent !== saisie)
    remarques.push('Votre couleur principale est trop sombre pour le fond de l\'application : elle a été éclaircie pour rester lisible.');

  // Les boutons portent un texte gras en capitales : le blanc reste lisible à partir de 3,5.
  // On le privilégie, c'est l'apparence attendue ; le noir prend le relais sur les couleurs claires.
  const accentText = contraste('#ffffff', accent) >= 3.5 ? '#ffffff' : '#111111';

  let accent2 = accent, chart2 = GRIS_COURBE, secondaireRetenue = false;
  if (hexValide(couleur2)) {
    const s = couleur2.toLowerCase();
    if (contraste(s, FOND) < 1.6) {
      // Noir ou presque : invisible. On dérive de la principale.
      accent2 = rgbVersHex(hexVersRgb(accent).map(v => v + (255 - v) * 0.45));
      remarques.push('Votre couleur secondaire est trop sombre pour être visible : une teinte dérivée de la principale est utilisée à la place.');
    } else {
      accent2 = eclaircir(s, 2.2);
    }
    chart2 = accent2;
    secondaireRetenue = true;

    const [, satSec] = rgbVersTsl(hexVersRgb(s));
    if (contraste(saisie, FOND) < 1.6 && contraste(s, FOND) >= 3 && satSec > 0.25)
      remarques.push('Votre couleur principale est très sombre. Inverser principale et secondaire mettrait davantage vos couleurs en valeur.');
  }

  return { accent, accentText, accent2, chart2, secondaireRetenue, remarques,
           accentRgb: hexVersRgb(accent).join(',') };
}

let courant = calculerTheme(DEFAUT.couleur, DEFAUT.couleur2);
export const themeCourant = () => courant;

/** Applique le thème à toute l'application. */
export function appliquerTheme(couleur, couleur2) {
  courant = calculerTheme(couleur, couleur2);
  const r = document.documentElement.style;
  r.setProperty('--accent',      courant.accent);
  r.setProperty('--accent-rgb',  courant.accentRgb);
  r.setProperty('--accent-text', courant.accentText);
  r.setProperty('--accent2',     courant.accent2);
  r.setProperty('--chart2',      courant.chart2);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', '#0d1117');
  window.dispatchEvent(new CustomEvent('theme-change', { detail: courant }));
  return courant;
}
